import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DialogFooter } from '@/components/ui/dialog';
import { useCreateGroundRent, useUpdateGroundRent, GroundRentRecord } from '@/hooks/useGroundRent';
import { toast } from 'sonner';
import { CurrencyToggle } from '@/components/currency-toggle/CurrencyToggle';

const schema = z.object({
  annual_amount: z.number().min(0.01, 'Amount must be greater than 0'),
  due_month: z.number().min(1).max(12),
  due_day: z.number().min(1).max(28),
  start_year: z.number().min(1900).max(2100),
  currency: z.string().min(1, 'Currency is required'),
  notes: z.string().nullable().optional(),
});

type FormData = z.infer<typeof schema>;

interface GroundRentFormProps {
  fixedAssetId: string;
  assetCurrency: string;
  existingRecord?: GroundRentRecord | null;
  onSuccess: () => void;
}

export function GroundRentForm({ fixedAssetId, assetCurrency, existingRecord, onSuccess }: GroundRentFormProps) {
  const { register, handleSubmit, watch, setValue, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      annual_amount: existingRecord ? Number(existingRecord.annual_amount) : 0,
      due_month: existingRecord?.due_month || 1,
      due_day: existingRecord?.due_day || 1,
      start_year: existingRecord?.start_year || new Date().getFullYear(),
      currency: existingRecord?.currency || assetCurrency,
      notes: existingRecord?.notes || '',
    }
  });

  const createRecord = useCreateGroundRent();
  const updateRecord = useUpdateGroundRent();

  const onSubmit = async (data: FormData) => {
    try {
      if (existingRecord) {
        await updateRecord.mutateAsync({
          id: existingRecord.id,
          fixed_asset_id: fixedAssetId,
          ...data,
          notes: data.notes || null,
        });
        toast.success('Ground rent obligation updated');
      } else {
        await createRecord.mutateAsync({
          fixed_asset_id: fixedAssetId,
          ...data,
          notes: data.notes || null,
        });
        toast.success('Ground rent obligation created');
      }
      onSuccess();
    } catch (error: any) {
      toast.error(`Failed to save ground rent: ${error.message}`);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="annual_amount">Annual Amount</Label>
          <Input
            id="annual_amount"
            type="number"
            step="0.01"
            {...register('annual_amount', { valueAsNumber: true })}
          />
          {errors.annual_amount && <p className="text-sm text-destructive">{errors.annual_amount.message}</p>}
        </div>
        <div className="space-y-2">
          <Label>Currency</Label>
          <div className="pt-2">
            <CurrencyToggle
              baseCurrency={assetCurrency}
              selectedCurrency={watch('currency')}
              onCurrencyChange={(val) => setValue('currency', val, { shouldValidate: true })}
              showBalance={false}
            />
          </div>
          {errors.currency && <p className="text-sm text-destructive">{errors.currency.message}</p>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="due_month">Due Month (1-12)</Label>
          <Input
            id="due_month"
            type="number"
            min={1}
            max={12}
            {...register('due_month', { valueAsNumber: true })}
          />
          {errors.due_month && <p className="text-sm text-destructive">{errors.due_month.message}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="due_day">Due Day (1-28)</Label>
          <Input
            id="due_day"
            type="number"
            min={1}
            max={28}
            {...register('due_day', { valueAsNumber: true })}
          />
          {errors.due_day && <p className="text-sm text-destructive">{errors.due_day.message}</p>}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="start_year">Start Year</Label>
        <Input
          id="start_year"
          type="number"
          {...register('start_year', { valueAsNumber: true })}
        />
        <p className="text-xs text-muted-foreground">The year this property first became liable for ground rent</p>
        {errors.start_year && <p className="text-sm text-destructive">{errors.start_year.message}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Notes (Optional)</Label>
        <Input
          id="notes"
          {...register('notes')}
        />
      </div>

      <DialogFooter>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save Obligation'}
        </Button>
      </DialogFooter>
    </form>
  );
}
